import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'
import type { TeamTreeNode } from './TeamTree'

interface DeleteTeamDialogProps {
  team: TeamTreeNode
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface PreconditionResult {
  can_delete: boolean
  blockers: string[]
}

export function DeleteTeamDialog({ team, open, onOpenChange }: DeleteTeamDialogProps) {
  const [preconditions, setPreconditions] = useState<PreconditionResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (open) {
      setLoading(true)
      setPreconditions(null)

      fetch(`/admin/teams/${team.id}/preconditions`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      })
        .then((res) => res.json())
        .then((data: PreconditionResult) => {
          setPreconditions(data)
          setLoading(false)
        })
        .catch(() => {
          setPreconditions({ can_delete: false, blockers: ['Failed to check preconditions'] })
          setLoading(false)
        })
    }
  }, [open, team.id])

  const handleDelete = () => {
    setProcessing(true)
    router.delete(`/admin/teams/${team.id}`, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
      onFinish: () => setProcessing(false),
    })
  }

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setPreconditions(null)
      setProcessing(false)
    }
    onOpenChange(value)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{team.name}&rdquo;</AlertDialogTitle>
          <AlertDialogDescription>
            {loading && 'Checking preconditions...'}
            {!loading && preconditions?.can_delete && (
              'This team will be archived. This cannot be undone.'
            )}
            {!loading && preconditions && !preconditions.can_delete && (
              'This team cannot be deleted due to the following:'
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!loading && preconditions && !preconditions.can_delete && (
          <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {preconditions.blockers.map((blocker, i) => (
              <li key={i}>{blocker}</li>
            ))}
          </ul>
        )}

        <Separator />

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={handleDelete}
            disabled={loading || !preconditions?.can_delete || processing}
          >
            {processing ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
